import { blake3 } from "@noble/hashes/blake3.js";

import { sign } from "../crypto/ed25519";
import { bytesToHex, encSigningCredBare, hexToBytes } from "../crypto/envelope";
import { getCachedDeriveMlDsa } from "../crypto/ml_dsa";
import { formatServerError } from "../lib/format/server_error";
import { DOMAIN_ACCOUNT_SELF_AUTH } from "../types/wire";
import {
  authStoreCredentialLegs,
  type AuthCredentialLegs,
} from "./auth_headers";
import { authedRequest } from "./authed_request";
import { dedupeInFlight } from "./inflight_dedupe";
import { randomRequestNonceHex } from "./request_nonce";

/**
 * Wallet self-polling wrapper for `POST /accounts/me/sync`.
 *
 * The backend response carries the cap-style 3-tier wallet view
 * (free / held / derived total) the FE needs to surface so a
 * reconnecting player understands their 진행 중 chips are still
 * theirs (just locked under an active table hold) instead of
 * misreading the free balance alone as "I lost everything".
 *
 * Auth: rides `DOMAIN_ACCOUNT_SELF_AUTH` (owner-scoped, no
 * table_id) so a signature captured here cannot be replayed
 * against a table-scoped write.
 *
 * Payload layout (byte-for-byte mirror of backend
 * `arkhe-holdem-server::auth::account_self_auth_payload`):
 *
 *     BLAKE3(DOMAIN_ACCOUNT_SELF_AUTH
 *            || player_id    (32 fixed bytes — ed25519 verifying key)
 *            || unix_ms_le   (8 little-endian bytes)
 *            || body_hash    (32 fixed bytes — BLAKE3 of request body)
 *            || [epoch_le]   (4 little-endian bytes — rotated cred only)
 *            || request_nonce(16 fixed bytes — FINAL field))
 *
 * `x-request-ts` is a plain `Date.now()` in MILLISECONDS (it only has to
 * land inside the server's ±300 s skew window). Per-request distinctness
 * comes from the fresh CSPRNG `x-request-nonce` appended as the payload's
 * FINAL field: two account-self reads signed in the same physical
 * millisecond — within one tab (the Lobby mount fires the RG, wallet, and
 * active-seats reads back-to-back over an otherwise-identical empty-body
 * payload) OR across two tabs of the same account — draw distinct nonces
 * => distinct signatures => distinct replay-cache keys, so the server's
 * single-use replay cache (keyed on the signature bytes) never rejects a
 * legitimate read as a captured replay. This subsumes the prior
 * monotonic-clock + per-tab-body-nonce + replay-retry scaffolding.
 */

const TEXT_ENCODER = new TextEncoder();

export interface AccountSelfAuthHeaders {
  readonly "x-player-id": string;
  readonly "x-player-sig": string;
  readonly "x-request-ts": string;
  readonly "x-request-nonce": string;
  readonly "x-signing-cred"?: string;
  readonly "x-ml-dsa-epoch"?: string;
  readonly "x-ml-dsa-sig"?: string;
}

export interface SignAccountSelfAuthHeadersArgs {
  /** Lowercase hex of the 32-byte ed25519 verifying key bound to
   *  the account row. */
  readonly playerIdHex: string;
  /** 32-byte ed25519 signing seed (the in-memory auth-store key). */
  readonly signingKey: Uint8Array;
  /** Request body the caller will send. Pass `""` for empty-body
   *  POSTs (the canonical `/accounts/me/sync` shape). */
  readonly body: string;
  /** Hybrid / rotated credential legs. OMITTED (undefined) => default-read
   *  the active legs from the auth slice (`authStoreCredentialLegs`); an
   *  EXPLICIT value (incl. `{}` for genesis) overrides — the enroll path
   *  passes `{}` to prove ownership with the genesis key only. */
  readonly creds?: AuthCredentialLegs;
  /** Optional clock override for vitest. Returns unix MILLISECONDS. */
  readonly now?: () => number;
}

function u64LE(n: bigint): Uint8Array {
  if (n < 0n || n > (1n << 64n) - 1n) {
    throw new RangeError(`u64LE: out of range: ${n}`);
  }
  const buf = new Uint8Array(8);
  let v = n;
  for (let i = 0; i < 8; i++) {
    buf[i] = Number(v & 0xffn);
    v >>= 8n;
  }
  return buf;
}

function concatBytes(...chunks: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

/**
 * Compute the canonical 32-byte BLAKE3 signing payload for an
 * account-self HTTP route auth triple. Exported so vitest can pin
 * the byte layout against the Rust mirror without going through
 * the signing path.
 */
function u32LE(n: number): Uint8Array {
  if (!Number.isInteger(n) || n < 0 || n > 0xffffffff) {
    throw new RangeError(`u32LE: out of u32 range: ${n}`);
  }
  const buf = new Uint8Array(4);
  buf[0] = n & 0xff;
  buf[1] = (n >>> 8) & 0xff;
  buf[2] = (n >>> 16) & 0xff;
  buf[3] = (n >>> 24) & 0xff;
  return buf;
}

export function accountSelfAuthPayload(args: {
  playerIdHex: string;
  unixMs: number;
  body: string;
  /** Epoch trailer — `u32LE(epoch)` appended ONLY for an explicit
   *  (rotated ed25519) credential; `undefined` => genesis (no trailer).
   *  Keyed off the ED25519 cred epoch, never the ml-dsa epoch. */
  signingEpoch?: number;
  /** Per-request nonce — 32 hex chars (16 CSPRNG bytes) appended as the
   *  FINAL field, AFTER any epoch trailer. `undefined` => no append
   *  (mirrors the BE `request_nonce: None` dual-accept branch). */
  requestNonceHex?: string;
}): Uint8Array {
  const playerId = hexToBytes(args.playerIdHex);
  if (playerId.length !== 32) {
    throw new RangeError(
      `accountSelfAuthPayload: playerId must be 32 bytes, got ${playerId.length}`,
    );
  }
  const bodyBytes = TEXT_ENCODER.encode(args.body);
  const bodyHash = blake3(bodyBytes, { dkLen: 32 });
  const chunks: Uint8Array[] = [
    DOMAIN_ACCOUNT_SELF_AUTH,
    playerId,
    u64LE(BigInt(args.unixMs)),
    bodyHash,
  ];
  if (args.signingEpoch !== undefined) {
    chunks.push(u32LE(args.signingEpoch));
  }
  if (args.requestNonceHex !== undefined) {
    const nonce = hexToBytes(args.requestNonceHex);
    if (nonce.length !== 16) {
      throw new RangeError(
        `accountSelfAuthPayload: requestNonce must be 16 bytes, got ${nonce.length}`,
      );
    }
    chunks.push(nonce);
  }
  return blake3(concatBytes(...chunks), { dkLen: 32 });
}

/**
 * Build the `{ "x-player-id", "x-player-sig", "x-request-ts" }`
 * triple for an account-self POST. Mirrors `signPlayerAuthHeaders`
 * except the BLAKE3 prefix rides `DOMAIN_ACCOUNT_SELF_AUTH` and the
 * payload omits the table_id (the self routes are owner-scoped).
 */
export function signAccountSelfAuthHeaders(
  args: SignAccountSelfAuthHeadersArgs,
): AccountSelfAuthHeaders {
  if (args.signingKey.length !== 32) {
    throw new RangeError(
      `signAccountSelfAuthHeaders: signingKey must be 32 bytes, got ${args.signingKey.length}`,
    );
  }
  // Plain wall clock: the per-request nonce (below) is the single-use
  // discriminator now, so `unix_ms` only has to land inside the server's
  // ±300 s skew window. An explicit `now` (the vitest pin) overrides.
  const unixMs = args.now !== undefined ? args.now() : Date.now();
  // Fresh CSPRNG nonce per call — folded into the payload as its FINAL
  // field + emitted as `x-request-nonce`. Two account-self reads signed
  // in the same physical ms (the Lobby mount fires the RG / wallet /
  // active-seats reads together over the IDENTICAL empty-body payload, or
  // two tabs of the same account) draw distinct nonces => distinct
  // signatures => distinct replay-cache keys, instead of byte-colliding
  // and tripping `auth.signature_replayed`.
  const requestNonceHex = randomRequestNonceHex();
  // OMITTED `creds` => default-read the active legs from the auth slice so
  // the account-self surface emits the same legs the WS lane does; an
  // explicit value (incl. `{}`) overrides (enroll genesis-only / vitest).
  const creds = args.creds ?? authStoreCredentialLegs();
  const edEpoch = creds.ed25519Epoch ?? 0;
  const signingEpoch = edEpoch > 0 ? edEpoch : undefined;
  const payload = accountSelfAuthPayload({
    playerIdHex: args.playerIdHex,
    unixMs,
    body: args.body,
    requestNonceHex,
    ...(signingEpoch !== undefined ? { signingEpoch } : {}),
  });
  const sig = sign(payload, args.signingKey);
  const headers: Record<string, string> = {
    "x-player-id": args.playerIdHex,
    "x-player-sig": bytesToHex(sig),
    "x-request-ts": String(unixMs),
    "x-request-nonce": requestNonceHex,
  };
  if (edEpoch > 0) {
    headers["x-signing-cred"] = bytesToHex(
      encSigningCredBare({
        epoch: edEpoch,
        kind: "ed25519",
        pubkey: hexToBytes(args.playerIdHex),
      }),
    );
  }
  if (creds.mlDsaSeed != null) {
    // Hybrid leg — read the boot-preloaded ml-dsa leaf so this builder
    // stays synchronous (its callers pass no `await`).
    const mlSig = getCachedDeriveMlDsa()(creds.mlDsaSeed).sign(payload);
    headers["x-ml-dsa-epoch"] = String(creds.mlDsaEpoch ?? 0);
    headers["x-ml-dsa-sig"] = bytesToHex(mlSig);
  }
  return headers as unknown as AccountSelfAuthHeaders;
}

/**
 * Cap-style wallet snapshot the `/accounts/me/sync` response
 * exposes. `free` = spendable off-table balance, `held` = sum of
 * active table holds, `total` = derived sum (the BE never sends
 * total directly — it stays a 1-line FE composition so the
 * conservation invariant is checkable at the consumer).
 */
export interface WalletSnapshot {
  readonly free: number;
  /** Chips at tables right now — the sum of live seat stacks. */
  readonly inPlay: number;
  readonly held: number;
  readonly total: number;
}

export interface FetchWalletSnapshotArgs {
  readonly serverBase: string;
  readonly playerIdHex: string;
  readonly signingKey: Uint8Array;
  readonly fetchImpl?: typeof fetch;
  readonly now?: () => number;
}

/**
 * `POST /accounts/me/sync` — empty-body call that returns the
 * 3-tier wallet view (+ RG + cash-mode sections we ignore here).
 * Triggered after login, on Lobby mount, after a table leave, on
 * WS reconnect, and on rebuy / wallet-mutate WS events so the
 * "지갑 + 진행 중" cap-style display always reflects the BE truth.
 */
export async function fetchWalletSnapshot(
  args: FetchWalletSnapshotArgs,
): Promise<WalletSnapshot> {
  // Coalesce concurrent reads for the same account so a double-mount
  // (StrictMode dev double-invoke, a fast re-login / reconnect) fires
  // ONE round-trip instead of two — see `fetchActiveSeats` for the
  // full rationale. A read that starts after the prior settled is not
  // served stale; the key releases on settle.
  return dedupeInFlight(`wallet_sync:${args.playerIdHex}`, () =>
    fetchWalletSnapshotUncoalesced(args),
  );
}

async function fetchWalletSnapshotUncoalesced(
  args: FetchWalletSnapshotArgs,
): Promise<WalletSnapshot> {
  // Empty body: the per-request signed nonce (drawn fresh inside
  // `signAccountSelfAuthHeaders`) is the single-use discriminator now, so
  // two tabs of the same account never sign byte-identical reads even with
  // an identical empty body. We sign the SAME string we send.
  const body = "";
  const response = await authedRequest({
    url: `${args.serverBase}/accounts/me/sync`,
    method: "POST",
    headers: { "content-type": "application/json" },
    signHeaders: () =>
      signAccountSelfAuthHeaders({
        playerIdHex: args.playerIdHex,
        signingKey: args.signingKey,
        body,
        ...(args.now !== undefined ? { now: args.now } : {}),
      }),
    body,
    ...(args.fetchImpl !== undefined ? { fetchImpl: args.fetchImpl } : {}),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(formatServerError(response.status, text));
  }
  const data = (await response.json()) as {
    wallet: {
      mode: string;
      balance_chips: number;
      in_play_chips: number;
      held_chips: number;
    };
  };
  const free = Number(data.wallet.balance_chips);
  const inPlay = Number(data.wallet.in_play_chips);
  const held = Number(data.wallet.held_chips);
  return {
    free,
    inPlay,
    held,
    total: free + held,
  };
}
